import {
  ConferenceData,
  FEATURE_CALL,
  FEATURE_RECORD_SOUND,
  FEATURE_SOUND,
  FEATURE_VIDEO,
  MediaDevicePreviewSource,
  PreferredMediaDevices,
  PreviewError,
  Videoconference,
  VideoConferenceProvider,
} from './definitions';
import { VideoconferenceFactoryProvider } from './providers';
import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { AgoraApiProviderService } from '@project/data-providers';
import { HcService } from '@project/services';
import { HCConfigFeatures } from 'src/hc-config/hc-config';
import { MediaDevicePreferencesService } from './services/media-device-preferences.service';

@Injectable({
  providedIn: 'root',
})
export class VideoconferenceImpl implements Videoconference, MediaDevicePreviewSource {
  private videoConferenceProvider: VideoConferenceProvider;

  constructor(
    private factoryProvider: VideoconferenceFactoryProvider,
    private agoraApiService: AgoraApiProviderService,
    private hcService: HcService,
    private mediaDevicePreferencesService: MediaDevicePreferencesService,
    private ngZone: NgZone,
  ) {}

  startProvider(conferenceData: ConferenceData): void {
    const enableSTT = this.hcService.hasFeature(HCConfigFeatures.AGORA_SPEECH_TO_TEXT);
    this.videoConferenceProvider = this.factoryProvider.getProvider(
      conferenceData,
      this.agoraApiService,
      enableSTT,
      this.ngZone,
    );
    this.videoConferenceProvider?.setPreferredDevices(this.mediaDevicePreferencesService.getPreferredDevices());
  }

  startDevicePreviewProvider(): void {
    this.videoConferenceProvider = this.factoryProvider.getDevicePreviewProvider(this.agoraApiService, this.ngZone);
    this.videoConferenceProvider.setPreferredDevices(this.mediaDevicePreferencesService.getPreferredDevices());
  }

  getRemoteParticipants(): (string | number)[] {
    return this.videoConferenceProvider.getRemoteParticipants();
  }

  remoteVideoObservable(): Observable<boolean> {
    return this.videoConferenceProvider.remoteVideoObservable;
  }

  remoteAudioObservable(): Observable<boolean> {
    return this.videoConferenceProvider.remoteAudioObservable;
  }

  localVideoObservable(): Observable<boolean> {
    return this.videoConferenceProvider.localVideoObservable;
  }

  localAudioObservable(): Observable<boolean> {
    return this.videoConferenceProvider.localAudioObservable;
  }

  getName(): string {
    return this.videoConferenceProvider.name;
  }

  async toggleSound(): Promise<void> {
    await this.videoConferenceProvider.toggleSound();
  }

  async toggleVideo(): Promise<void> {
    await this.videoConferenceProvider.toggleVideo();
  }

  async join(): Promise<void> {
    await this.videoConferenceProvider.join();
  }

  async left(): Promise<void> {
    await this.videoConferenceProvider.left();
  }

  remoteFallbackObservable(): Observable<boolean> {
    return this.videoConferenceProvider.remoteFallbackObservable;
  }

  previewErrorObservable(): Observable<PreviewError | null> {
    return this.videoConferenceProvider.previewErrorObservable;
  }

  localMicVolumeObservable(): Observable<number> {
    return this.videoConferenceProvider.localMicVolumeObservable;
  }

  devicesChangedObservable(): Observable<void> {
    return this.videoConferenceProvider.devicesChangedObservable;
  }

  setLocalVideoContainer(container: HTMLElement | null): void {
    this.videoConferenceProvider.setLocalVideoContainer(container);
  }

  getActiveDevices(): PreferredMediaDevices {
    return this.videoConferenceProvider.getActiveDevices();
  }

  async createLocalPreviewTracks(): Promise<void> {
    await this.videoConferenceProvider.createLocalPreviewTracks();
  }

  async stopLocalPreview(): Promise<void> {
    await this.videoConferenceProvider.stopLocalPreview();
  }

  async listCameras(): Promise<MediaDeviceInfo[]> {
    return this.videoConferenceProvider.listCameras();
  }

  async listMicrophones(): Promise<MediaDeviceInfo[]> {
    return this.videoConferenceProvider.listMicrophones();
  }

  async listSpeakers(): Promise<MediaDeviceInfo[]> {
    return this.videoConferenceProvider.listSpeakers();
  }

  async switchCamera(deviceId: string): Promise<void> {
    await this.videoConferenceProvider.switchCamera(deviceId);
  }

  async switchMicrophone(deviceId: string): Promise<void> {
    await this.videoConferenceProvider.switchMicrophone(deviceId);
  }

  switchSpeaker(deviceId: string): void {
    this.videoConferenceProvider.switchSpeaker(deviceId);
  }

  getFeatures(): string[] {
    return this.videoConferenceProvider.features();
  }

  hasFeature(name: string) {
    return this.getFeatures().includes(name);
  }

  hasFeatureVideo(): boolean {
    return this.hasFeature(FEATURE_VIDEO);
  }

  hasFeatureSound(): boolean {
    return this.hasFeature(FEATURE_SOUND);
  }

  hasFeatureRecordSound(): boolean {
    return this.hasFeature(FEATURE_RECORD_SOUND);
  }

  hasFeatureCall(): boolean {
    return this.hasFeature(FEATURE_CALL);
  }
}
